import { Transfer } from '@/modules/note/v1/entities/Transfer'
import { EnumTransferState } from '@/contracts/modules/note/enum/EnumTransferState'
import { EnumNoteFileError } from '@/contracts/modules/note/enum/EnumNoteFileError'

/** Сколько передач идёт одновременно; остальные ждут в состоянии QUEUED */
export const TRANSFERS_AT_ONCE = 4

export type TransferTask = (transfer: Transfer) => Promise<void>

/** Ошибка передачи с кодом для пользователя; остальные ошибки считаются сетевыми */
export class TransferError extends Error {
  constructor(public readonly code: EnumNoteFileError) {
    super(code)
  }
}

/** Отмена передачи пользователем: передача завершается состоянием CANCELLED, не ошибкой */
export class TransferCancelled extends Error {
  constructor() {
    super('cancelled')
  }
}

/**
 * Одна очередь модуля: не больше TRANSFERS_AT_ONCE задач одновременно, остальные ждут.
 * Задача сама следит за отменой через transfer.isCancelRequested() и бросает TransferCancelled.
 */
export class TransferQueue {
  private waiting: { transfer: Transfer, task: TransferTask }[] = []
  private running = 0

  constructor(private onChange: () => void = () => undefined) {}

  add(transfer: Transfer, task: TransferTask): void {
    this.waiting.push({ transfer, task })
    this.next()
  }

  /** Отменить ждущую передачу сразу, идущую — попросить остановиться */
  cancel(transfer: Transfer): void {
    const index = this.waiting.findIndex((item) => item.transfer === transfer)
    if (index !== -1) {
      this.waiting.splice(index, 1)
      transfer._setState(EnumTransferState.CANCELLED)
      this.onChange()
      return
    }
    transfer._requestCancel()
  }

  private next(): void {
    while (this.running < TRANSFERS_AT_ONCE && this.waiting.length > 0) {
      const item = this.waiting.shift() as { transfer: Transfer, task: TransferTask }
      this.running++
      item.transfer._setState(EnumTransferState.RUNNING)
      this.onChange()
      item.task(item.transfer)
        .then(() => item.transfer._setState(EnumTransferState.DONE))
        .catch((error) => item.transfer._setState(...this.failure(error)))
        .finally(() => {
          this.running--
          this.onChange()
          this.next()
        })
    }
  }

  private failure(error: unknown): [EnumTransferState, EnumNoteFileError | null] {
    if (error instanceof TransferCancelled) {
      return [EnumTransferState.CANCELLED, null]
    }
    if (error instanceof TransferError) {
      return [EnumTransferState.FAILED, error.code]
    }
    return [EnumTransferState.FAILED, EnumNoteFileError.NETWORK]
  }
}
